import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCog, faCrown, faEye } from '@fortawesome/free-solid-svg-icons';
import { UserRole } from '../types';

interface RoleUpdateModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (emailId: string, role: UserRole) => Promise<void>;
    userEmail: string;
    userName: string;
    currentRole?: UserRole;
}

export const RoleUpdateModal: React.FC<RoleUpdateModalProps> = ({
    open,
    onClose,
    onSubmit,
    userEmail,
    userName,
    currentRole = 'viewer',
}) => {
    const [selectedRole, setSelectedRole] = useState<UserRole>(currentRole);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Update selectedRole when currentRole prop changes (when modal opens with different user)
    useEffect(() => {
        if (open && currentRole) {
            setSelectedRole(currentRole);
        }
    }, [open, currentRole]);

    const handleSubmit = async () => {
        if (selectedRole === currentRole) {
            onClose();
            return;
        }

        setLoading(true);
        setError('');

        try {
            await onSubmit(userEmail, selectedRole);
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update user role');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedRole(currentRole);
        setError('');
        onClose();
    };

    const getRoleIcon = (role: UserRole) => {
        return role === 'manager' ? faCrown : faEye;
    };

    const getRoleColor = (role: UserRole) => {
        return role === 'manager' ? '#ff9800' : '#2196f3';
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon icon={faUserCog} />
                    Update User Role
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Update the role for <strong>{userName}</strong> ({userEmail})
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Current Role:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FontAwesomeIcon 
                            icon={getRoleIcon(currentRole)} 
                            style={{ color: getRoleColor(currentRole) }}
                        />
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                textTransform: 'capitalize',
                                fontWeight: 500,
                                color: getRoleColor(currentRole),
                            }}
                        >
                            {currentRole}
                        </Typography>
                    </Box>
                </Box>

                <FormControl fullWidth>
                    <InputLabel>New Role</InputLabel>
                    <Select
                        value={selectedRole}
                        label="New Role"
                        onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                        disabled={loading}
                    >
                        <MenuItem value="viewer">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FontAwesomeIcon icon={faEye} style={{ color: '#2196f3' }} />
                                <Typography>Viewer</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem value="manager">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FontAwesomeIcon icon={faCrown} style={{ color: '#ff9800' }} />
                                <Typography>Manager</Typography>
                            </Box>
                        </MenuItem>
                    </Select>
                </FormControl>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Role Permissions:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Viewer:</strong> Can view and preview templates
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Manager:</strong> Can view, edit, share, and manage user access
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={selectedRole === currentRole || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faUserCog} />}
                >
                    {loading ? 'Updating...' : 'Update Role'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 